/**
 * interpolation-directives.ts
 * Copyright (c) 2017, My Coverage Plan, Inc.
 * All rights reserved.
 */

import * as moment from "moment";
// import * as numeral from "numeral";
import { Variable } from "../../aggregation/typed-parameter";
import { DataType, IntegerTypes, NumberTypeOrder, NumberTypes }
  from "../../common/data/data-types";
import { oneline, sequentialPairs } from "../../support/utilities/functions";

// TODO temp until real deal!
declare var numeral : (input : number) => {format : (str : string) => string};

let VAR_INCREMENTER : number = 0;
export function wrap<T extends DataType> (
  type : T,
  value : any,
  cardinality : number = 1
) : Variable<T>
{
  return {
    name : `wrapped value ${VAR_INCREMENTER++}`,
    type,
    cardinality,
    collectionType : (cardinality === 1 ? "none" : "list"),
    value
  };
  // Note we don't allow wrapped sets with this function.
}

function comparableTypes (a : DataType, b : DataType) : boolean
{
  // TODO: invoke cardinality
  if (a === "parseable" || b === "parseable")
  {
    return true;
  }
  if (NumberTypes.has(a) && NumberTypes.has(b))
  {
    return true;
  }
  return a === b;
  // TODO: this is sufficient for server types, but not extended spectrum
}

function supertypeOf (a : DataType, b : DataType) : boolean
{
  if (a === b)
  {
    return true;
  }
  if (b === "parseable")
  {
    return true;
  }
  if (a === "string")
  {
    return true;
  }
  if (NumberTypes.has(a) && NumberTypes.has(b))
  {
    return NumberTypeOrder.indexOf(a) > NumberTypeOrder.indexOf(b);
  }
  if (a === "datetime" && b === "date")
  {
    return true;
  }
  // if (a === "string" && b === "html")
  // {
  //     return true;
  // }
  return false;
}

const AVAIL_DATE_FORMAT : string = "YYYY/MM/DD HH:mm:ss.SSS ZZ";
function parseAvailMoment (timestamp : string) : moment.Moment
{
  return moment(timestamp, AVAIL_DATE_FORMAT);
}
function parseAvailDate (timestamp : string) : Date
{
  return parseAvailMoment(timestamp).toDate();
}
function parseAs (value : string, type : DataType) : any
{
  if (value === "null")
  {
    return null;
  }
  switch (type)
  {
    case "string":
      return value;
    case "integer":
    case "whole number":
    case "natural number":
      return Number.parseInt(value, 10);
    case "float":
      return Number.parseFloat(value);
    case "boolean":
      return `${value}`.toLocaleLowerCase() === "true";
    case "date":
      return parseAvailDate(value);
    default:
      throw new Error("Cannot yet parse for non-server datatypes!");
  }
}
function parseVarAs<T extends DataType> (
  variable : Variable<any>,
  type : T
) : Variable<T>
{
  return Object.assign(
    {},
    variable,
    {
      type,
      value: extractParsed(variable, type, true)
    }
  ) as Variable<T>;
}
function extractParsed (
  variable : Variable<any>,
  type : DataType,
  preserveCardinality : boolean = false
) : any
{
  if (type === "string" && variable.type !== "string")
  {
    if (preserveCardinality
      && variable.cardinality !== 1
      && Array.isArray(variable.value))
    {
      return variable.value.map(
        (v : any) => DefaultDirective.execute(wrap(variable.type, v)));
    }
    return DefaultDirective.execute(variable);
  }
  if (variable.type === "parseable")
  {
    if (variable.cardinality !== 1)
    {
      throw new Error(`Non-scalar parseables not supported`);
    }
    return parseAs(variable.value, type);
  }
  return variable.value;
}

type RangeChecker = (x : number) => boolean;
export function inRange (
  min : number = Number.MIN_SAFE_INTEGER,
  max : number = Number.MAX_SAFE_INTEGER,
) : RangeChecker
{
  return (x : number) => (x >= min && x <= max);
}

function compareParseableString (variable : Variable<any>, compare : string)
  : number
{
  if (variable.value === null)
  {
    return null;
  }
  switch (variable.type)
  {
    case "string":
      return `${variable.value}`.localeCompare(compare);
    case "integer":
    case "whole number":
    case "natural number":
      return variable.value - Number.parseInt(compare, 10);
    case "float":
      return variable.value - Number.parseFloat(compare);
    case "boolean":
      console.error("Bad comparison: inequality on boolean");
      return null;
    case "date":
      return +variable.value - +parseAvailDate(compare);
    default:
      throw new Error(`Extended type comparsion unsupported`);
  }
}

// tslint:disable-next-line:cyclomatic-complexity
function compareInType (a : any, b : any, type : DataType) : number
{
  if (a === null)
  {
    return Number.MIN_SAFE_INTEGER;
  }
  if (b === null)
  {
    return Number.MAX_SAFE_INTEGER;
  }
  if (a === b)
  {
    return 0;
  }
  switch (type)
  {
    case "string":
      return `${a}`.localeCompare(b);
    case "integer":
    case "whole number":
    case "natural number":
    case "float":
      return a - b;
    case "boolean":
      return a ? 1 : -1;
    case "date":
      return +a - +b;
    default:
      throw new Error(`Extended type comparsion unsupported`);
  }
}

interface Valued<T>
{
  value : T;
}

/**
 * A {@link Directive} is a function that formats, transforms, or processes data
 * as part of the templating engine and microsyntax. Each subclass is generally
 * one type of function, and is instantiated into the parse tree in order to
 * capture parameters and eject rendering functions.
 *  - I is the input type for the directive (i.e. the type of the target)
 *  - P is the type of the parameters for the directive (after validation)
 *  - O is the output type of the directive
 */
export abstract class Directive<
  I extends Variable<any>,
  P extends any[],
  O
> {
  /**
   * The name identifies the directive, and should match the parsed string
   * that triggers it.
   */
  public abstract readonly name : string | symbol;

  public abstract returnType () : DataType;

  protected _targetType : DataType;
  public get targetType () : DataType
  {
    if (this._targetType)
    {
      return this._targetType;
    }
    throw Error("Interpolation target type not yet set!");
  }
  public set targetType (type : DataType)
  {
    this._targetType = type;
  }

  // public parameterize : (variables : Array<Variable<any>>) => P;

  protected abstract parameterRange : RangeChecker;
  protected abstract parameterTypes : Array<DataType | "match">;
  protected parameterTypeDefault : DataType = "string";
  protected targetCoercionType : DataType = "string";

  protected initialParamCheck (
    types : DataType[],
    targetType : DataType,
    cardinalities? : number[]
  ) : void
  {
    if (targetType === "parseable")
    {
      if (this.targetCoercionType)
      {
        targetType = this.targetCoercionType;
        console.warn(
          `Target type is parseable. Coercing to ${targetType}`);
      }
      else
      {
        throw new Error(oneline`
          Directive's target type was a parseable<any> and coercion
          is forbidden by directive.`);
      }
    }
    this.targetType = targetType;
    if (!this.parameterRange(types.length))
    {
      throw new Error(`Incorrect number of parameters ${types}`);
    }
    if (this.parameterTypes
      && !this.parameterRange(this.parameterTypes.length))
    {
      throw new Error(`Type configuration does not match param range`);
    }
  }
  public verifyParameters (
    types : DataType[],
    targetType : DataType,
    cardinalities : number[]
  ) : void
  {
    this.initialParamCheck(types, targetType);
    for (let i : number = 0; i < this.parameterTypes.length; i++)
    {
      const provided : DataType = types[i];
      if (!provided)
      {
        break;
      }
      const goal : DataType =
        this.parameterTypes[i] === "match"
        ? targetType
        : this.parameterTypes[i] as DataType;
      if (!supertypeOf(goal, provided))
      {
        throw new Error(
          `Provided ${provided} cannot be used as ${goal} parameter`);
      }
    }
  }

  public parseParameters (params : Array<Variable<any>>) : P
  {
    const output : any[] = [];
    for (let i : number = 0; i < params.length; i++)
    {
      const variable : Variable<any> = params[i] as Variable<any>;
      const type : DataType =
        (i in this.parameterTypes)
        ? ((this.parameterTypes[i] === "match")
          ? this.targetType
          : this.parameterTypes[i] as DataType)
        : this.parameterTypeDefault;
      output.push(extractParsed(variable, type));
    }
    return output as P;
  }

  public parseTarget (target : Variable<any>) : I
  {
    return target as I;
    // return parseVarAs(target, this.targetCoercionType) as I;
  }

  public abstract execute (
    variable : I,
    parameters : P
  ) : O;

  // public eject () : ItemInterpolator<I, O>
  // {
  //     return (target : I, variables : Array<Variable<any>>) =>
  //         this.execute(target, this.parameters(variables), variables);
  // }
}

// class VariableNameDirective extends ArgumentLimitedDirective
// {
//     public readonly name : string = "debug,name";
//     public static readonly isProtocolDirective : boolean = false;
//     protected readonly maxArgs : number = 0;
//     public execute (value : StdVariable) : string
//     {
//         return value.name;
//     }
// }

abstract class StringDirective<P extends any[]>
  extends Directive<Variable<any>, P, string>
{
  public returnType () : DataType
  {
    return "string";
  }
}

abstract class PureDirective<I extends Variable<any>, O>
  extends Directive<I, never[], O>
{
  protected parameterRange : RangeChecker = inRange(0, 0);
  protected parameterTypes : DataType[] = [];
}

abstract class PureStringDirective extends StringDirective<never[]>
{
  protected parameterRange : RangeChecker = inRange(0, 0);
  protected parameterTypes : DataType[] = [];
}

function leafEntry (
  dc : DirectiveClass,
  allowedTypes? : DataType[]
) : DirectiveEntry
{
  if (allowedTypes && allowedTypes.indexOf("string") === -1)
  {
    return {
      terminates : true,
      hasSubdirectives : false,
      isProtocolDirective : true,
      byType : (type : DataType) =>
        (allowedTypes.indexOf(type) > -1 ? dc : null)
    };
  }
  else
  {
    return {
      terminates : true,
      hasSubdirectives : false,
      isProtocolDirective : true,
      byType : (type : DataType) => dc
    };
  }
}

export class PassThroughDirective<T extends DataType>
  extends PureDirective<Variable<T>, Variable<T>>
{
  public readonly name : string = "passthrough";
  public returnType () : DataType
  {
    return this.targetType;
  }
  public execute (
    variable : Variable<T>,
    parameters : never[]
  ) : Variable<T>
  {
    return variable;
  }

  public static Entry : DirectiveEntry = leafEntry(PassThroughDirective);
}

export class DefaultDirective extends PureStringDirective
{
  public readonly name : symbol = Symbol.for("default");

  public execute (variable : Variable<any>) : string
  {
    return DefaultDirective.execute(variable);
  }

  public static NullString : string = "«unknown»";
  public static ReplaceIfNull (value : any) : any | string
  {
    return (value === null) ? DefaultDirective.NullString : value;
  }
  public static execute (variable : Variable<any>) : string
  {
    if (variable.value === null)
    {
      return DefaultDirective.NullString;
    }
    if (variable.cardinality !== 1)
    {
      if (!Array.isArray(variable.value))
      {
        console.error(
          oneline`Variable with cardinality ${variable.cardinality}
          had non-array, non-null value ${variable.value}`);
        return DefaultDirective.NullString;
      }
      return variable.value.map(
        (value : any) => DefaultDirective.execute({
          value,
          type: variable.type,
          cardinality: 1,
          name: "",
          collectionType: "none"
        })).join(", ");
    }
    if (variable.type === "boolean")
    {
      return (variable.value === true) ? "true" : "false";
    }
    if (variable.type === "date")
    {
      return new Date(variable.value).toLocaleDateString(
        undefined, {timeZone: "UTC"});
    }
    if (typeof variable.value === "symbol")
    {
      throw new Error(`Symbol atom leaked to stringifier`);
    }
    return `${variable.value}`;
  }

  public static Entry : DirectiveEntry = leafEntry(DefaultDirective);
}

// class CountDirective extends PureStringDirective
// {

// }

abstract class ComparativeDirective
  extends Directive<Variable<any>, [any, string, string], string>
{
  protected parameterRange : RangeChecker = inRange(3, 3);
  protected parameterTypes : Array<DataType | "match"> =
    ["match", "string", "string"];
  public returnType () : DataType
  {
    return "string";
  }
}

class EqualsDirective extends ComparativeDirective
{
  public readonly name : string = "=";

  public execute (
    variable : Variable<any>,
    [compare, positive, negative] : [any, string, string]
  ) : string
  {
    if ((compare === null || compare === "null")
      && variable.value === null)
    {
      return positive;
    }
    return (
      compareInType(variable.value, compare, variable.type) === 0
      ? positive
      : negative);
  }

  public static Entry : DirectiveEntry = leafEntry(EqualsDirective);
}

class NotEqualsDirective extends ComparativeDirective
{
  public readonly name : string = "≠";

  public execute (
    variable : Variable<any>,
    [compare, positive, negative] : [string, string, string]
  ) : string
  {
    if ((compare === null || compare === "null")
      && variable.value === null)
    {
      return negative;
    }
    return (
      compareInType(variable.value, compare, variable.type) !== 0
      ? positive
      : negative);
  }

  public static Entry : DirectiveEntry = leafEntry(NotEqualsDirective);
}

abstract class BucketedComparisonDirective
  extends StringDirective<Array<[any, string]>>
{
  protected parameterRange : RangeChecker =
    ((x : number) => (x > 0) && (x % 2 === 1));
  protected parameterTypes : null = null;

  public verifyParameters (types : DataType[], targetType : DataType) : void
  {
    this.initialParamCheck(types, targetType);
    const last : DataType = types[types.length - 1];
    if ((types.length % 2 === 1) && !supertypeOf("string", types.pop()))
    {
      throw new Error(`Default value parameter must be string`);
    }
    for (let i : number = 0; i < types.length; i += 2)
    {
      if (!supertypeOf(targetType, types[i])
        || !supertypeOf("string", types[i + 1]))
      {
        throw new Error(
          `Bucket params must alternate ${targetType} and string`);
      }
    }
  }
  public parseParameters (params : Array<Variable<any>>)
    : Array<[any, string]>
  {
    if (params.length % 2 === 1)
    {
      params.splice(
        -1,
        0,
        {type: "string", value: Symbol.for("default")} as Variable<any>
      );
    }
    const buckets : Array<[any, string]> =
      sequentialPairs(params).map(
        ([item, output] : [Variable<any>, Variable<any>]) => [
          extractParsed(item, this.targetType),
          extractParsed(output, "string")
        ] as [any, string]);
    return buckets;
  }

  protected abstract returnIfGreater : boolean;
  protected abstract returnIfEquals : boolean;

  public execute (
    variable : Variable<any>,
    params : Array<[any, string]>
  ) : string
  {
    if (variable.value === null)
    {
      return params[params.length - 1][1];
    }
    for (const [compare, output] of params.slice(0, -1))
    {
      const delta : number =
        compareInType(variable.value, compare, variable.type);
      if (delta === null)
      {
        break;
      }
      if (
        (delta > 0) && this.returnIfGreater
        || (delta < 0) && !this.returnIfGreater
        || (delta === 0) && this.returnIfEquals
      )
      {
        return output;
      }
    }
    return params[params.length - 1][1];
  }

  public static Allowed : DataType[] = [
    "float", "integer", "whole number", "natural number", "string", "date"
  ];
}

class GreaterThanDirective extends BucketedComparisonDirective
{
  public readonly name : string = ">";
  protected returnIfEquals : boolean = false;
  protected returnIfGreater : boolean = true;
  public static Entry : DirectiveEntry =
    leafEntry(GreaterThanDirective, BucketedComparisonDirective.Allowed);
}
class GreaterThanOrEqualDirective extends BucketedComparisonDirective
{
  public readonly name : string = "≥";
  protected returnIfEquals : boolean = true;
  protected returnIfGreater : boolean = true;
  public static Entry : DirectiveEntry = leafEntry(
      GreaterThanOrEqualDirective,
      BucketedComparisonDirective.Allowed);
}
class LessThanDirective extends BucketedComparisonDirective
{
  public readonly name : string = "<";
  protected returnIfEquals : boolean = false;
  protected returnIfGreater : boolean = false;
  public static Entry : DirectiveEntry =
    leafEntry(LessThanDirective, BucketedComparisonDirective.Allowed);
}
class LessThanOrEqualDirective extends BucketedComparisonDirective
{
  public readonly name : string = "≤";
  protected returnIfEquals : boolean = true;
  protected returnIfGreater : boolean = false;
  public static Entry : DirectiveEntry = leafEntry(
      LessThanOrEqualDirective,
      BucketedComparisonDirective.Allowed);
}

// TODO: verify
class MapDirective extends BucketedComparisonDirective
{
  public readonly name : string = "map";
  protected parameterRange : RangeChecker = inRange(1);
  protected returnIfGreater : null = null;
  protected returnIfEquals : null = null;

  public execute (
    variable : Variable<any>,
    params : Array<[any, string]>
  ) : string
  {
    for (const [compare, output] of params)
    {
      if (compare === Symbol.for("default")
        || compareInType(variable.value, compare, variable.type) === 0)
      {
        return output;
      }
    }
    return DefaultDirective.execute(variable);
  }

  public static Entry : DirectiveEntry = leafEntry(MapDirective);
}

const FormatDirectiveEntry : DirectiveEntry = {
  terminates: true,
  hasSubdirectives: false,
  isProtocolDirective: true,
  byType: (type : DataType) => {
    if (NumberTypes.has(type))
    {
      return FormatNumberDirective;
    }
    if (type === "date")
    {
      return FormatTimestampDirective;
    }
    return null;
  }
};
class FormatTimestampDirective extends StringDirective<[string]>
{
  public readonly name : string = "format";
  protected parameterRange : RangeChecker = inRange(1, 1);
  protected parameterTypes : DataType[] = ["string"];

  public execute (
    variable : Variable<any>,
    [format] : [string]
  ) : string
  {
    if (variable.value === null)
    {
      return DefaultDirective.NullString;
    }
    return moment.utc(variable.value).format(format);
  }
}
class FormatNumberDirective extends StringDirective<[string]>
{
  public readonly name : string = "format";
  protected parameterRange : RangeChecker = inRange(1, 1);
  protected parameterTypes : DataType[] = ["float"];

  public execute (
    variable : Variable<any>,
    [format] : [string]
  ) : string
  {
    if (variable.value === null)
    {
      return DefaultDirective.NullString;
    }
    return numeral(variable.value).format(format);
  }
}

class IndexDirective extends StringDirective<string[]>
{
  public readonly name : string = "index";
  protected parameterRange : RangeChecker = inRange(2); // TODO is 1 valid?
  protected parameterTypes : DataType[] = ["string"];

  protected variableMode : boolean;

  public verifyParameters (
    types : DataType[],
    targetType : DataType,
    cardinalities : number[]
  ) : void
  {
    this.initialParamCheck(types, targetType);
    if (!supertypeOf("string", types[0]))
    {
      throw new Error(`Index default must be string`);
    }
    if (cardinalities[1] !== 1)
    {
      this.variableMode = true;
      if (types.length > 2)
      {
        throw new Error(
          `Variable mode index should only have two params`);
      }
    }
    else
    {
      this.variableMode = false;
      if (types.slice(1).some(
        (type : DataType) => !supertypeOf("string", type)))
      {
        throw new Error(`All lexical list entries must be strings`);
      }
    }
  }
  public parseParameters (params : Array<Variable<any>>) : string[]
  {
    if (this.variableMode)
    {
      const output : string[] =
        extractParsed(params[1], "string", true);
      output.unshift(extractParsed(params[0], "string"));
    }
    else
    {
      return params.map(
        (v : Variable<any>) => extractParsed(v, "string"));
    }
  }

  public execute (variable : Variable<any>, params : string[]) : string
  {
    const index : number =
      (Number.isInteger(variable.value) && variable.value in params)
      ? variable.value
      : 0;
    return params[index];
  }

  public static Entry : DirectiveEntry =
    leafEntry(IndexDirective, ["natural number"]);
}

class UppercaseDirective extends PureStringDirective
{
  public readonly name : string = "uppercase";
  public execute (variable : Variable<any>) : string
  {
    variable = parseVarAs(variable, "string");
    if (variable.value === null)
    {
      variable.value = DefaultDirective.NullString;
    }
    return `${variable.value}`.toLocaleUpperCase();
  }

  public static Entry : DirectiveEntry =
    leafEntry(UppercaseDirective, ["string"]);
}

class LowercaseDirective extends PureStringDirective
{
  public readonly name : string = "lowercase";
  public execute (variable : Variable<any>) : string
  {
    variable = parseVarAs(variable, "string");
    if (variable.value === null)
    {
      variable.value = DefaultDirective.NullString;
    }
    return `${variable.value}`.toLocaleLowerCase();
  }

  public static Entry : DirectiveEntry =
    leafEntry(LowercaseDirective, ["string"]);
}

class CapitalizeDirective extends PureStringDirective
{
  public readonly name : string = "capitalize";
  public execute (variable : Variable<any>) : string
  {
    variable = parseVarAs(variable, "string");
    if (variable.value === null)
    {
      variable.value = DefaultDirective.NullString;
    }
    return (variable.value.slice(0, 1).toLocaleUpperCase()
      + variable.value.slice(1));
  }

  public static Entry : DirectiveEntry =
    leafEntry(CapitalizeDirective, ["string"]);
}

class TitleCaseDirective extends PureStringDirective
{
  public readonly name : string = "titlecase";
  public execute (variable : Variable<any>) : string
  {
    variable = parseVarAs(variable, "string");
    if (variable.value === null)
    {
      variable.value = DefaultDirective.NullString;
    }
    return variable.value.replace(
      /(^|\s)\w/g,
      (match : string) => match.toLocaleUpperCase());
  }
  public static Entry : DirectiveEntry =
    leafEntry(TitleCaseDirective, ["string"]);
}

class InflectIndefiniteArticleDirective extends PureStringDirective
{
  public readonly name : string = "inflect,indefinite article";
  public execute (variable : Variable<any>) : string
  {
    variable = parseVarAs(variable, "string");
    if (variable.value === null)
    {
      return `an ${DefaultDirective.NullString}`;
    }
    return (
      (variable.value.match(/^[aeiouAEIOU]/) ? "an " : "a ")
      + variable.value);
  }
  public static Entry : DirectiveEntry =
    leafEntry(InflectIndefiniteArticleDirective, ["string"]);
}

class InflectDefiniteArticleDirective extends PureStringDirective
{
  public readonly name : string = "inflect,definite article";
  protected readonly maxArgs : number = 0;
  public execute (variable : Variable<any>) : string
  {
    variable = parseVarAs(variable, "string");
    if (variable.value === null)
    {
      return `the ${DefaultDirective.NullString}`;
    }
    return `the ${variable.value}`;
  }
  public static Entry : DirectiveEntry =
    leafEntry(InflectDefiniteArticleDirective, ["string"]);
}

enum Gender
{
  Female,
  Male,
  Other
}
abstract class GenderDirective extends PureStringDirective
{
  protected abstract values : Map<Gender, string>;
  protected getGender (str : string) : Gender
  {
    if (str.toLowerCase() === "male")
    {
      return Gender.Male;
    }
    if (str.toLowerCase() === "female")
    {
      return Gender.Female;
    }
    return Gender.Other;
  }
  public execute (variable : Variable<any>) : string
  {
    variable = parseVarAs(variable, "string");
    if (variable.value === null)
    {
      return this.values.get(Gender.Other);
    }
    return this.values.get(this.getGender(variable.value));
  }
}
class GenderedSubject extends GenderDirective
{
  public readonly name : string = "inflect,gendered,subject";
  protected values : Map<Gender, string> = new Map([
    [Gender.Female, "she"],
    [Gender.Male,   "he"],
    [Gender.Other,  "they"]
  ]);
  public static Entry : DirectiveEntry =
    leafEntry(GenderedSubject, ["string"]);
}
class GenderedObject extends GenderDirective
{
  public readonly name : string = "inflect,gendered,object";
  protected values : Map<Gender, string> = new Map([
    [Gender.Female, "her"],
    [Gender.Male,   "him"],
    [Gender.Other,  "them"]
  ]);
  public static Entry : DirectiveEntry =
    leafEntry(GenderedObject, ["string"]);
}
class GenderedPossessiveAdjective extends GenderDirective
{
  public readonly name : string = "inflect,gendered,possessive adjective";
  protected values : Map<Gender, string> = new Map([
    [Gender.Female, "her"],
    [Gender.Male,   "his"],
    [Gender.Other,  "their"]
  ]);
  public static Entry : DirectiveEntry =
    leafEntry(GenderedPossessiveAdjective, ["string"]);
}
class GenderedPossessivePronoun extends GenderDirective
{
  public readonly name : string = "inflect,gendered,possessive pronoun";
  protected values : Map<Gender, string> = new Map([
    [Gender.Female, "hers"],
    [Gender.Male,   "his"],
    [Gender.Other,  "theirs"]
  ]);
  public static Entry : DirectiveEntry =
    leafEntry(GenderedPossessivePronoun, ["string"]);
}
class GenderedReflexiveObject extends GenderDirective
{
  public readonly name : string = "inflect,gendered,reflexive object";
  protected values : Map<Gender, string> = new Map([
    [Gender.Female, "herself"],
    [Gender.Male,   "himself"],
    [Gender.Other,  "themself"]
  ]);
  public static Entry : DirectiveEntry =
    leafEntry(GenderedReflexiveObject, ["string"]);
}

class InflectPossessiveDirective extends PureStringDirective
{
  public readonly name : string = "inflect,possessive";
  protected irregulars : Map<string, string> = new Map([
    ["it",      "its"],
    ["he",      "his"],
    ["she",     "her"],
    ["they",    "their"]
  ]);
  public execute (variable : Variable<any>) : string
  {
    variable = parseVarAs(variable, "string");
    if (variable.value === null)
    {
      return DefaultDirective.NullString;
    }
    const irregular : string = this.irregulars.get(variable.value);
    if (irregular)
    {
      return irregular;
    }
    else
    {
      return (
        variable.value.match(/s$/)
        ? `${variable.value}'`
        : `${variable.value}'s`);
    }
  }
  public static Entry : DirectiveEntry =
    leafEntry(InflectPossessiveDirective, ["string"]);
}

class InflectPluralDirective extends StringDirective<[number]>
{
  public readonly name : string = "inflect,plural";
  protected parameterRange : RangeChecker = inRange(1, 1);
  protected parameterTypes : DataType[] = ["float"];

  protected massNounList : Set<string> = new Set([
    "water",
    "glass",
    //
  ]);
  protected isMassNoun (noun : string) : boolean
  {
    return false;
  }
  protected irregulars : Map<string, string> = new Map([
    ["foot", "feet"],
    ["tooth", "teeth"]
  ]);
  protected irregularCategories : Array<[RegExp, string]> = [
    [/[^aeo]y$/, "$1ies"],
    [/fe?$/, "ves"],
    [/man$/, "men"],
    [/(s|sh|ch|x)$/, "$1es"],
  ];
  public execute (variable : Variable<any>, [count] : [number]) : string
  {
    variable = parseVarAs(variable, "string");
    if (variable.value === null)
    {
      return DefaultDirective.NullString;
    }
    if (count <= 1 || this.isMassNoun(variable.value))
    {
      return variable.value;
    }
    const irregular : string | null = this.irregulars.get(variable.value);
    if (irregular)
    {
      return irregular;
    }
    this.irregularCategories.forEach(
      ([re, replacement] : [RegExp, string]) => {
        const replaced : string =
          (variable.value as string).replace(re, replacement);
        if (replaced !== variable.value)
        {
          return replaced;
        }
      }
    );
    return `${variable.value}s`;
  }
  public static Entry : DirectiveEntry =
    leafEntry(InflectPluralDirective, ["string"]);
}

class ConjugateCopulaDirective extends PureStringDirective
{
  public readonly name : string = "inflect,copula";
  public execute (variable : Variable<any>) : string
  {
    if (variable.value === null)
    {
      return DefaultDirective.NullString;
    }
    return variable.value > 1 ? "are" : "is";
  }
  public static Entry : DirectiveEntry =
    leafEntry(InflectPluralDirective, Array.from(NumberTypes));
}

type MapToString = ((item : any, index : number, arr : any[]) => string);

abstract class JoinDirective<P extends any[]> extends StringDirective<P>
{
  protected preprocess (value : any[], params : P) : any[]
  {
    return value;
  }
  protected blockPrefix : string = "";
  protected blockSuffix : string = "";
  protected delimiter : string = "";
  protected wrapperBuilder (params : P) : MapToString
  {
    return (item : any) => `${item}`;
  }
  public execute (variable : Variable<any>, params : P) : string
  {
    if (variable.value === null)
    {
      return DefaultDirective.NullString;
    }
    if (variable.collectionType === "set")
    {
      // Sort sets to maintain consistent order
      (variable.value as any[]).sort(
        (a : Variable<any>, b : Variable<any>) =>
          compareInType(a.value, b.value, a.type));
    }
    const items : string[] =
      this.preprocess(variable.value, params).map(
        this.wrapperBuilder(params));
    return this.blockPrefix + items.join(this.delimiter) + this.blockSuffix;
  }
}
class SimpleJoinDirective extends JoinDirective<[string]>
{
  public readonly name : string = "join,simple";
  protected parameterRange : RangeChecker = inRange(0, 1);
  protected parameterTypes : DataType[] = ["string"];
  protected preprocess (arr : any[], [delim] : [string]) : any[]
  {
    this.delimiter = delim || ", ";
    return arr;
  }

  public static Entry : DirectiveEntry =
    leafEntry(SimpleJoinDirective);
}
class BookendedJoinDirective extends JoinDirective<[string, string]>
{
  public readonly name : string = "join,bookends";
  protected parameterRange : RangeChecker = inRange(2, 2);
  protected parameterTypes : DataType[] = ["string", "string"];
  protected wrapperBuilder ([prefix, suffix] : [string, string]) : MapToString
  {
    return (item : any) => `${prefix}${item}${suffix}`;
  }

  public static Entry : DirectiveEntry =
    leafEntry(BookendedJoinDirective);
}
class OxfordJoinDirective extends JoinDirective<[string]>
{
  public readonly name : string = "join,oxford";
  protected parameterRange : RangeChecker = inRange(1, 1);
  protected parameterTypes : DataType[] = ["string"];
  protected preprocess (arr : any[], [conjunction] : [string]) : any[]
  {
    this.delimiter = ", ";
    switch (arr.length)
    {
      case 0:
      case 1:
        return arr;
      case 2:
        this.delimiter = ` ${conjunction} `;
        return arr;
      default:
        arr.push(`${conjunction} ${arr.pop()}`);
        return arr;
    }
  }
  public static Entry : DirectiveEntry =
    leafEntry(OxfordJoinDirective);
}
abstract class HtmlListDirective extends JoinDirective<[string[], string[]]>
{
  protected parameterRange : RangeChecker = inRange(0);
  protected parameterTypes : null = null;

  protected usesCountParameter : boolean;
  public verifyParameters (
    types : DataType[],
    targetType : DataType,
    cardinalities : number[]
  ) : void
  {
    this.initialParamCheck(types, targetType);
    this.usesCountParameter = supertypeOf("integer", types[0]);
    // All of the types are string or could be string, so checks are useless
  }

  public parseParameters (params : Array<Variable<any>>)
    : [string[], string[]]
  {
    // let itemAttrs : string[] = [];
    // let listAttrs : string[] = params;
    const listAttrCount : number =
      this.usesCountParameter
      ? extractParsed(params.shift(), "integer")
      : params.length;
    const attrs : string[] = params.map(
      (v : Variable<any>) => extractParsed(v, "string"));
    return [attrs.slice(0, listAttrCount), attrs.slice(listAttrCount)];
  }

  protected abstract listTag : string;
  protected blockSuffix : string = `</${this.listTag}>`;
  protected wrapperBuilder ([listAttrs, itemAttrs] : [string[], string[]])
    : MapToString
  {
    this.blockPrefix = `<${this.listTag} ${listAttrs.join(" ")}>`;
    return (item : any) => `<li ${itemAttrs.join(" ")}>${item}</li>`;
  }
}
class UnorderedListDirective extends HtmlListDirective
{
  public readonly name : string = "join,unordered";
  protected readonly listTag : string = "ul";
  public static Entry : DirectiveEntry = leafEntry(UnorderedListDirective);
}
class OrderedListDirective extends HtmlListDirective
{
  public readonly name : string = "join,ordered";
  protected readonly listTag : string = "ol";
  public static Entry : DirectiveEntry = leafEntry(OrderedListDirective);
}
class TruncatedListDirective extends JoinDirective<[number, string]>
{
  public readonly name : string = "join,truncated";
  protected parameterRange : RangeChecker = inRange(2, 2);
  protected parameterTypes : DataType[] = ["integer", "string"];
  protected readonly delimiter : string = ", ";
  protected preprocess (
    value : any[],
    [count, ellipsisText] : [number, string]
  ) : any[]
  {
    if (value.length > count)
    {
      this.blockSuffix = ellipsisText;
      return value.slice(0, count);
    }
    else
    {
      this.blockSuffix = "";
      return value;
    }
  }
  public static Entry : DirectiveEntry = leafEntry(TruncatedListDirective);
}

class ZipDirective extends JoinDirective<[string[]]>
{
  public name : string = "zip";
  protected parameterRange : RangeChecker = inRange(1, 1);
  protected parameterTypes : DataType[] = ["string"];

  protected readonly delimiter : string = "), ";
  protected readonly blockSuffix : string = ")";
  protected readonly zipConnector : string = " (";

  protected preprocess (
    value : any[],
    [toPair] : [string[]]
  ) : any[]
  {
    toPair = toPair || [];
    return value.map(
      (v : any, i : number) =>
        `${v}${this.zipConnector}${(i < toPair.length) ? toPair[i] : "?"}`);
  }
  public static Entry : DirectiveEntry = leafEntry(ZipDirective);
}

class ShowProgressDirective extends StringDirective<[number, number, string]>
{
  public readonly name : string = "show-progress";
  protected parameterRange : RangeChecker =
    ((x : number) => (x === 0 || x === 2 || x === 3));
  protected parameterTypes : DataType[] = ["float", "float", "string"];
  public execute (
    variable : Variable<any>, [min, max, label] : [number, number, string]
  ) : string
  {
    min = Number.isFinite(min) ? min : 0;
    max = Number.isFinite(max) ? max : 100;
    label = (typeof label === "string" && label.length !== 0)
      ? label
      : `${variable.value}`;
    const fill : number =
      Number.isFinite(variable.value)
      ? 100 * (variable.value - min) / max
      : 0;
    const barStyle : string =
      fill < 100 / 3
      ? "danger"
      : (fill < 200 / 3
      ? "warning"
      : "success");
    return `
      <div class="progress">
        <div
          class="progress-bar progress-bar-${barStyle}"
          role="progressbar"
          aria-valuenow="${variable.value}"
          style="min-width: 5px; width: ${fill}%;"
          aria-valuemin="${min}"
          aria-valuemax="${max}">
        </div>
        <label>${label}</label>
      </div>
      `;
  }

  public static Entry : DirectiveEntry =
    leafEntry(ShowProgressDirective, Array.from(NumberTypes));
}

class TagWrappedDirective extends StringDirective<string[]>
{
  public readonly name : string = "tag-for";
  protected parameterRange : RangeChecker = inRange(1);
  protected parameterTypes : DataType[] = ["string"];

  public execute (
    variable : Variable<any>, entries : string[]
  ) : string
  {
    variable = parseVarAs(variable, "string");
    const tag : string = variable.value || "div";
    console.warn(entries);
    return (entries
      .filter((e : string) => e && e.replace("&#xA;", "").trim().length > 0)
      .map((e : string) => `<${tag}>${e}</${tag}>`)
      .join(""));
  }

  public static Entry : DirectiveEntry =
    leafEntry(TagWrappedDirective, ["string"]);
}

export interface DirectiveClass
{
  new () : Directive<any, any, any>;
}

export interface DirectiveEntry
{
  terminates : boolean;
  hasSubdirectives : boolean;
  isProtocolDirective : boolean;
  byType (type : DataType) : DirectiveClass;
}

const INTERNAL_NODE : DirectiveEntry = {
  terminates: false,
  hasSubdirectives: true,
  isProtocolDirective: true,
  byType: () => null
};

export const Directives : Map<(string | symbol), DirectiveEntry> = new Map([
  [Symbol.for("default"), DefaultDirective.Entry],
  ["passthrough", PassThroughDirective.Entry],
  // ["debug", Superdirective],
  // ["debug,name", VariableNameDirective],

  ["=", EqualsDirective.Entry],
  ["≠", NotEqualsDirective.Entry],
  [">", GreaterThanDirective.Entry],
  ["≥", GreaterThanOrEqualDirective.Entry],
  ["<", LessThanDirective.Entry],
  ["≤", LessThanOrEqualDirective.Entry],
  ["map", MapDirective.Entry],
  ["format", FormatDirectiveEntry], // selects by type
  ["index", IndexDirective.Entry],
  ["uppercase", UppercaseDirective.Entry],
  ["lowercase", LowercaseDirective.Entry],
  ["titlecase", TitleCaseDirective.Entry],
  ["capitalize", CapitalizeDirective.Entry],

  ["inflect", INTERNAL_NODE],
  ["inflect,indefinite article", InflectIndefiniteArticleDirective.Entry],
  ["inflect,definite article", InflectDefiniteArticleDirective.Entry],
  ["inflect,possessive", InflectPossessiveDirective.Entry],
  ["inflect,plural", InflectPluralDirective.Entry],
  ["inflect,copula", ConjugateCopulaDirective.Entry],

  ["inflect,gendered", INTERNAL_NODE],
  ["inflect,gendered,subject", GenderedSubject.Entry],
  ["inflect,gendered,object", GenderedObject.Entry],
  ["inflect,gendered,possessive adjective",
    GenderedPossessiveAdjective.Entry],
  ["inflect,gendered,possessive pronoun", GenderedPossessivePronoun.Entry],
  ["inflect,gendered,reflexive object", GenderedReflexiveObject.Entry],

  ["join", INTERNAL_NODE],
  ["join,simple", SimpleJoinDirective.Entry],
  ["join,bookends", BookendedJoinDirective.Entry],
  ["join,oxford", OxfordJoinDirective.Entry],
  ["join,unordered", UnorderedListDirective.Entry],
  ["join,ordered", OrderedListDirective.Entry],
  ["join,truncated", TruncatedListDirective.Entry],
  ["zip", ZipDirective.Entry],

  ["tag-for", TagWrappedDirective.Entry],

  ["show-progress", ShowProgressDirective.Entry]
] as Array<[string | symbol, DirectiveEntry]>);



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/server/validation/interpolation-directives.ts